import { CurrencyPipe } from '@angular/common';
import { Component, DestroyRef, HostListener, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { PlatformEventsService } from 'src/app/core/services/platform-events.service';
import { ResponsiveService } from 'src/app/core/services/responsive.service';
import { HubspotEventNameEnum, HubspotEventProperties } from 'src/app/models/hubspot-tracking-event';
import { RowItem } from 'src/app/ui-kit/text-accordion/text-accordion.component';

import { RoiCalculatorService } from '../../../../core/services/roi-calculator.service';
import { UserService } from '../../../../core/services/user.service';
import { EppReadyFlowService } from '../../../../early-pay-requests/epp-ready-flow/epp-ready-flow.service';
import { EppReadyFlowStepsEnum } from '../../../../early-pay-requests/models/epp-ready.model';
import { FormFieldInputComponent } from '../../../../ui-kit/form-field-input/form-field-input.component';
import { LogoComponent } from '../../../../ui-kit/logo/logo.component';
import { FrameWithTitleComponent } from '../../../../ui-kit/marketing-collateral/frame-with-title/frame-with-title.component';
import { TextAccordionComponent } from '../../../../ui-kit/text-accordion/text-accordion.component';
import { IconTileComponent } from '../../../icon-tile/icon-tile.component';
import { EarlyPayPlatformCalculatorOutputCardComponent } from '../../early-pay-platform-calculator/early-pay-platform-calculator-output-card/early-pay-platform-calculator-output-card.component';

@Component({
  selector: 'app-roi-calculator',
  templateUrl: './roi-calculator.component.html',
  styleUrls: ['./roi-calculator.component.scss'],
  providers: [CurrencyPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButtonModule,
    RouterLink,
    FormFieldInputComponent,
    EarlyPayPlatformCalculatorOutputCardComponent,
    MatIconModule,
    IconTileComponent,
    FrameWithTitleComponent,
    TextAccordionComponent,
    LogoComponent,
  ],
})
export class RoiCalculatorComponent implements OnInit {
  private platformEventsService = inject(PlatformEventsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private responsiveService = inject(ResponsiveService);
  roiCalculatorService = inject(RoiCalculatorService);
  private eppReadyFlowService = inject(EppReadyFlowService);
  private userService = inject(UserService);
  private destroyRef = inject(DestroyRef);

  paymentDays = 45;
  advancePercentage = 50;

  cashAdvance = 0;
  constraforsFee = 0;
  paidWithGc = 0;
  totalPayout = 0;

  paymentDaysList: number[] = [30, 45, 60, 75, 90, 105, 120];
  advanceAmountList: number[] = [25, 50, 70, 75, 80];
  yearlyRevenueControl: FormControl<number> = new FormControl<number>(100000, { nonNullable: true });
  monthlyInvoiceControl: FormControl<number> = new FormControl<number>(100000, { nonNullable: true });

  showCalculations = false;
  isMobileView = false;
  containerMaxWidth = 1100;
  containerSidePadding = 16;
  comparisonBlockWidth: number;
  comparisonHorizontalPadding = 16;
  mobileViewContainerTopPadding = 50;

  faqs: RowItem[] = [
    {
      question: 'How can I use Early Pay to increase my Gross Profit?',
      answer:
        'Early Pay boosts gross profit by speeding up cash flow—get invoice advances in ~48 hours instead of waiting 30–90+ days. That lets you: <br/>&nbsp;&nbsp;&bull;&nbsp;&nbsp;Take on more projects sooner <br/>&nbsp;&nbsp;&bull;&nbsp;&nbsp;Win supplier discounts with upfront payment <br/>&nbsp;&nbsp;&bull;&nbsp;&nbsp;Avoid high-interest financing <br/>&nbsp;&nbsp;&bull;&nbsp;&nbsp;Pay on time and skip late fees <br/><br/>Faster cash in → smoother ops, higher margins, and quicker growth.',
    },
    {
      question: 'How does this Calculator work?',
      answer:
        'Here’s how the Early Pay Calculator works:<br/>&nbsp;&nbsp;&bull;&nbsp;&nbsp;Enter basics: annual revenue, invoice amount, advance amount, net payment terms.<br/>&nbsp;&nbsp;&bull;&nbsp;&nbsp;Compare results and view a breakdown (advance, payout, fees).<br/>&nbsp;&nbsp;&bull;&nbsp;&nbsp;If it pencils out, apply and get funded in ~48 hours.\n\nThis is an estimate only; actual results vary by business.',
    },
    {
      question: 'Does Constrafor reduce their Early Pay fees?',
      answer:
        'Yes! Speak to our funding consultants on upgrading your Early Pay subscription tier, you can book a call based on your availability.',
    },
  ];

  @HostListener('window:resize')
  onWindowResize(): void {
    this.comparisonBlockWidth = this.getComparisonBlockWidth();
  }

  ngOnInit(): void {
    this.yearlyRevenueControl.setValue(this.roiCalculatorService.yearlyRevenueControl.value);
    this.monthlyInvoiceControl.setValue(this.roiCalculatorService.monthlyInvoiceControl.value);
    this.watchControlChanges();
    this.recalculateValues();
    this.watchResponsiveChanges();
    this.comparisonBlockWidth = this.getComparisonBlockWidth();
    this.userService
      .checkAndMarkPageAsVisited('roi-calculator-page')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  recalculateValues(): void {
    const advanceAmount = (this.advancePercentage / 100) * this.roiCalculatorService.monthlyInvoiceControl.value;
    const parsedPaymentDays = Number(this.paymentDays);
    const subscriptionTierPercentage = 0.004;
    const advanceRate = Math.ceil((parsedPaymentDays / 7) * subscriptionTierPercentage * 10000) / 10000;
    const eppFee = advanceAmount * advanceRate;
    const remainder = this.roiCalculatorService.monthlyInvoiceControl.value - advanceAmount;
    this.paidWithGc = remainder;

    const platformFeePercentage = 0.01;
    const platformFee = platformFeePercentage * advanceAmount;
    this.constraforsFee = platformFee + eppFee;
    this.cashAdvance = advanceAmount - this.constraforsFee;
    this.totalPayout = this.cashAdvance + remainder;
  }

  onYearlyRevenueChange(newValue: Event | number): void {
    if (typeof newValue === 'number') {
      this.roiCalculatorService.yearlyRevenueControl.setValue(newValue);
    } else {
      const { value } = newValue.target as HTMLInputElement;
      this.roiCalculatorService.yearlyRevenueControl.setValue(Number(value) || 0);
    }
    this.recalculateValues();
  }

  onMonthlyInvoiceChange(newValue: Event | number): void {
    if (typeof newValue === 'number') {
      this.roiCalculatorService.monthlyInvoiceControl.setValue(newValue);
    } else {
      const { value } = newValue.target as HTMLInputElement;
      this.roiCalculatorService.monthlyInvoiceControl.setValue(Number(value) || 0);
    }
    this.recalculateValues();
  }

  onAdvancePercentageChange(percentage: number): void {
    this.advancePercentage = percentage;
    this.recalculateValues();
  }

  setPaymentDays(days: number): void {
    this.paymentDays = days;
    this.recalculateValues();
  }

  onFundedCtaClicked(): void {
    this.eppReadyFlowService.updateCurrentStepInUser(EppReadyFlowStepsEnum.PREVIEW_STEPS);
    this.router.navigate(['/', 'early-pay', 'epp-ready', 'preview-steps']);
  }

  private getSalesLink(): string {
    const currentPath = this.router.url.split('?')[0];
    return `https://hs.constrafor.com/meetings/alexis-vail/epp-ae-inbound-meeting-product?from=${currentPath}`;
  }

  onEmailMyEstimatesCtaClicked(): void {
    window.location.href = this.getSalesLink();
  }

  showEstimates(): void {
    this.showCalculations = true;
    const payload: HubspotEventProperties = {
      annual_revenue: this.roiCalculatorService.yearlyRevenueControl.value,
      invoice_amount: this.roiCalculatorService.monthlyInvoiceControl.value,
      annual_gross_profit: 0,
      net_payment_days: this.paymentDays,
      advance_amount_pct: this.advancePercentage,
      net_funding_amount: this.cashAdvance,
      balance_amount: this.paidWithGc,
      total_payout: this.totalPayout,
      constrafor_fee: this.constraforsFee,
    };
    this.platformEventsService.trackHubspotCustomEvent(HubspotEventNameEnum.ROI_CALCULATOR_RESULTS, payload);
  }

  private getComparisonBlockWidth(): number {
    const sidePanelWidth = this.isMobileView ? 0 : 87;
    const screenWidth = Math.min(window.screen?.width, window.innerWidth);
    const paddingsTotal = 2 * this.containerSidePadding + 2 * this.comparisonHorizontalPadding;
    const comparisonBlockWidth = screenWidth - sidePanelWidth - paddingsTotal;
    const maxComparisonBlockWidth = this.containerMaxWidth - paddingsTotal;
    return Math.min(comparisonBlockWidth, maxComparisonBlockWidth);
  }

  private watchControlChanges(): void {
    this.yearlyRevenueControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((value) => value !== null),
      )
      .subscribe((value) => {
        this.onYearlyRevenueChange(value);
      });
    this.monthlyInvoiceControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((value) => value !== null),
      )
      .subscribe((value) => {
        const valueNumber = parseFloat(value.toString().replace(/[$,]/g, ''));
        this.onMonthlyInvoiceChange(valueNumber);
      });
  }

  private watchResponsiveChanges(): void {
    this.responsiveService
      .isMobileScreen()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isMobileView) => {
        const isViewChanged = this.isMobileView !== isMobileView;
        this.isMobileView = isMobileView;
        if (isViewChanged) {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { mobile: this.isMobileView.toString() },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }
      });
  }
}
